import {
  GetActiveSupportChatsResponse,
  GetChatMessagesResponse,
} from "@/api/chats/types";
import { connectChatSocket, socket } from "@/api/socket";
import { useGetMeQuery } from "@/queries";
import { useSupportContext } from "@/stores";
import { Chat, Message } from "@/types";
import { LocalStorageService, QueryKey, ResourceName } from "@/utils";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useAccess } from "./access.hook";

type InfiniteQueryData<T> = {
  pages: T[];
  pageParams: number[];
};

export const addChatMessagesQuery = (
  newMessage: Message,
  queryClient: QueryClient
) => {
  queryClient.setQueryData<InfiniteQueryData<GetChatMessagesResponse>>(
    [QueryKey.CHAT_MESSAGES, newMessage.chat.id],
    (oldData) => {
      if (!oldData) return oldData;

      const updatedPages = [...oldData.pages];
      const newMessages = [newMessage, ...updatedPages[0].data];

      updatedPages[0] = {
        ...updatedPages[0],
        data: newMessages,
      };

      return {
        ...oldData,
        pages: updatedPages,
      };
    }
  );
};

export const changeReadMessageStatusQuery = (
  newMessage: Message,
  queryClient: QueryClient
) => {
  queryClient.setQueryData<InfiniteQueryData<GetChatMessagesResponse>>(
    [QueryKey.CHAT_MESSAGES, newMessage.chat.id],
    (oldData) => {
      if (!oldData) return oldData;

      const updatedPages = oldData.pages.map((page) => {
        return {
          ...page,
          data: page.data.map((message) => {
            if (message.id === newMessage.id) {
              return {
                ...message,
                ...newMessage,
              };
            }

            return message;
          }),
        };
      });

      return {
        ...oldData,
        pages: updatedPages,
      };
    }
  );
};

export const updateChatAfterReadMessage = (
  { isReadChanged }: { isReadChanged: boolean },
  message: Message,
  queryClient: QueryClient
) => {
  queryClient.setQueryData<InfiniteQueryData<GetActiveSupportChatsResponse>>(
    [QueryKey.ACTIVE_SUPPORT_CHATS],
    (oldData) => {
      if (!oldData) return oldData;

      const updatedPages = oldData.pages.map((page) => {
        return {
          ...page,
          data: page.data.map((chat) => {
            if (chat.id === message.chat.id) {
              if (isReadChanged) {
                return {
                  ...chat,
                  lastMessage: { ...chat.lastMessage, isRead: message.isRead },
                  unreadMessagesCount: message.isRead
                    ? Math.max(chat.unreadMessagesCount - 1, 0)
                    : chat.unreadMessagesCount + 1,
                };
              }
            }

            return chat;
          }),
        };
      });

      return {
        ...oldData,
        pages: updatedPages,
      };
    }
  );
};

export const updateMessageChatAfterNewMessage = (
  newMessage: Message,
  queryClient: QueryClient
) => {
  queryClient.setQueryData<InfiniteQueryData<GetActiveSupportChatsResponse>>(
    [QueryKey.ACTIVE_SUPPORT_CHATS],
    (oldData) => {
      if (!oldData) return oldData;
      let existedChat: Chat | undefined;
      const isSenderMe =
        newMessage.sender.id === LocalStorageService.getSupportUserId();

      const updatedPages = oldData.pages.map((page) => {
        return {
          ...page,
          data: page.data.filter((chat) => {
            if (chat.id === newMessage.chat.id) {
              existedChat = chat;

              return false;
            }
            return true;
          }),
        };
      });

      if (existedChat) {
        updatedPages[0]?.data.unshift({
          ...existedChat,
          unreadMessagesCount: isSenderMe
            ? existedChat.unreadMessagesCount
            : existedChat.unreadMessagesCount + 1,
          lastMessage: newMessage,
        });
      }

      return {
        ...oldData,
        pages: updatedPages,
      };
    }
  );
};

export const useInitializeChat = () => {
  const { me } = useGetMeQuery();
  const { canView } = useAccess();
  const { currentChat } = useSupportContext();

  const queryClient = useQueryClient();

  useEffect(() => {
    if (!me) {
      socket.disconnect();
      return;
    }

    const isAllowedChatConnection = canView(ResourceName.SUPPORT);
    if (isAllowedChatConnection && socket.active) {
      return;
    }

    if (isAllowedChatConnection) {
      connectChatSocket();
    } else {
      socket.disconnect();
    }
  }, [me?.id]);

  useEffect(() => {
    socket.on("message-created", (newMessage) => {
      addChatMessagesQuery(newMessage, queryClient);
      updateMessageChatAfterNewMessage(newMessage, queryClient);
    });
    socket.on("message-updated", ({ message, isReadChanged }) => {
      changeReadMessageStatusQuery(message, queryClient);
      updateChatAfterReadMessage({ isReadChanged }, message, queryClient);
    });

    return () => {
      socket.off("message-created");
      socket.off("message-updated");
    };
  }, [currentChat]);
};
